<template>
  <div class="bg-white">
    <div
      class="row mx-0 calendar-header"
      :class="{ 'calendar-header-month': type === 'month' }"
    >
      <div class="col d-flex justify-content-start">
        <RsButton
          class="mx-2"
          variant="secondary"
          hollow
          @click="focus = ''"
        >
          {{ todayLabel }}
        </RsButton>
        <div v-if="hasDefaultSyncButton">
          <RsButton
            variant="secondary"
            icon="sync-alt"
            @click="handleSyncButtonClick()"
          >
            Sync
          </RsButton>
          <RsIcon
            id="sync"
            weight="fas"
            class="rs-calendar__sync ml-2"
            icon="question-circle"
          />
          <RsTooltip
            target="sync"
            title="This will sync your calendar"
            position="right"
            :delay="{ show: 200 }"
          />
        </div>
        <slot name="customSyncButton" />
      </div>
      <div class="col d-flex justify-content-center align-items-center">
        <RsNavigationButton
          id="previous"
          icon="arrow-square-left"
          description="Previous"
          @click="prev"
        />
        <span
          v-if="calendarTitle"
          class="mx-2 rs-calendar__title"
        >{{ calendarTitle }}</span>
        <RsNavigationButton
          id="next"
          @click="next"
        />
      </div>
      <div class="col d-flex justify-content-end mr-2">
        <RsButton
          variant="secondary"
          :hollow="type !== 'day'"
          class="btn-no-border-right"
          @click="getViewType('day')"
        >
          {{ dayLabel }}
        </RsButton>
        <RsButton
          variant="secondary"
          :hollow="type !== 'week'"
          class="btn-no-border"
          @click="getViewType('week')"
        >
          {{ weekLabel }}
        </RsButton>
        <RsButton
          variant="secondary"
          :hollow="type !== 'month'"
          class="btn-no-border-left"
          @click="getViewType('month')"
        >
          {{ monthLabel }}
        </RsButton>
      </div>
    </div>
    <div :style="[type !== 'month' ? { height: height } : {}, boxHeightCSS]">
      <VCalendar
        ref="calendar"
        v-model="focus"
        class="mt-2 rs-calendar"
        color="primary"
        :events="events"
        event-overlap-mode="stack"
        :first-time="firstTime"
        :weekday-format="formatEventDay"
        :type="type"
        :locale="$i18n.locale"
        @click:event="showEvent"
        @click:date="viewDay"
        @change="updateRange"
      >
        <template #event="{ event }">
          <div
            :style="event.eventBlockCustomClass"
            :class="{ 'event-default': event.type === 'tour', 'event-exception': event.type === 'exception' }"
            class="pl-2 event-block"
          >
            <div class="font-weight-bold rs-calendar-event--truncate">
              {{ event.name }}
            </div>
            <div class="rs-calendar-event--truncate">
              {{ formatStartEndTime(event) }}
            </div>
          </div>
        </template>
      </VCalendar>
    </div>
    <RsCalendarEventModal
      v-if="isUseDefaultEventModal"
      :show-event-modal="showEventModal"
      :event-in-view="eventInView"
      @on-modal-close="handleEventModalClose"
      @on-edit-event-block="handleEventBlockEdit"
      @on-delete-event-block="handleEventBlockDelete"
      @on-show-event-modal="handleShowEventModal"
    >
      <template #event-details="slotProps">
        <slot
          name="event-details"
          :details="slotProps.details"
        />
      </template>
    </RsCalendarEventModal>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { CalendarTimestamp } from 'vuetify';
import dayjs from 'dayjs';
import { ICalendarEvent } from '@/shared/interfaces/calendar-event';

export type TCalendarEmitData = {
  events: ICalendarEvent[],
  event: ICalendarEvent,
};

export default defineComponent({
  name: 'Calendar',

  props: {
    /**
     * Boolean to indicate whether to show/hide sync button
     */
    hasDefaultSyncButton: {
      type: Boolean,
      default: true,
    },
    /**
     * List of event objects to display in the calendar
     */
    events: {
      type: Array as PropType<ICalendarEvent[]>,
      default: () => [],
    },
    /**
     * Additional calendar details that may be useful for
     * syncing the calendar
     */
    calendarDetails: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },
    /**
     * Boolean to indicate whether to show default event modal
     * upon event block click
     */
    isUseDefaultEventModal: {
      type: Boolean,
      default: true,
    },

    /**
     * String representing the height of the calendar in
     * week and day calendar
     */
    height: {
      type: String,
      default: 'auto',
    },

    /**
     * String representing the min height of the
     * calendar box
     */
    boxMinHeight: {
      type: Number,
    },

    /**
     * String representing the min height of the
     * calendar box
     */
    firstTime: {
      type: String,
      default: '00:00',
    },

    /**
     * Label for the "Today" navigation button
     */
    todayLabel: {
      type: String,
      default: 'Today',
    },

    /**
     * Label for the "Day" view toggle button
     */
    dayLabel: {
      type: String,
      default: 'Day',
    },

    /**
     * Label for the "Week" view toggle button
     */
    weekLabel: {
      type: String,
      default: 'Week',
    },

    /**
     * Label for the "Month" view toggle button
     */
    monthLabel: {
      type: String,
      default: 'Month',
    },

  },
  data() {
    return {
      focus: '' as string,
      type: 'month' as string,
      typeToLabel: {
        month: 'Month',
        week: 'Week',
        day: 'Day',
      } as Record<string, string>,
      showEventModal: false as boolean,
      eventInView: {} as ICalendarEvent,
      calendarTitle: '' as string,
    };
  },
  computed: {
    calendarInstance(): Vue & { prev: () => void; next: () => void; checkChange: () => void;
      start: string; title: string; } {
      return this.$refs.calendar as Vue & { prev: () => void; next: () => void; checkChange: () => void;
        start: string; title: string;};
    },
    boxHeightCSS() {
      return {
        '--box-min-height-day': `${this.boxMinHeight}px`,
        '--box-min-height-week': `${(this.boxMinHeight ?? 0) + 20}px`,
      };
    },
  },
  mounted() {
    this.calendarInstance.checkChange();
    this.calendarTitle = this.calendarInstance.title;
  },

  methods: {
    formatStartEndTime(event: ICalendarEvent): string {
      return `${dayjs(`${event.start}`).format('h:mm a')}${event.start && event.end ? '-' : ''} ${dayjs(`${event.end}`).format('h:mm a')}`;
    },
    formatEventDay(dateObject: ICalendarEvent): string {
      return dayjs(`${dateObject.date}`).locale(this.$i18n.locale).format('dddd');
    },
    formatToFullDate(date: string): string {
      return dayjs(`${date}`).locale(this.$i18n.locale).format('MMMM D, YYYY');
    },
    formatToYear(date:string): string {
      return dayjs(`${date}`).format('YYYY');
    },
    formatToMonth(date:string):string {
      return dayjs(`${date}`).locale(this.$i18n.locale).format('MMMM');
    },
    formatToDayYear(date:string):string {
      return dayjs(`${date}`).format('D, YYYY');
    },
    formatToMonthDay(date:string):string {
      return dayjs(`${date}`).locale(this.$i18n.locale).format('MMMM D');
    },
    formatCalendarWeekTitle(startDate: string, endDate: string): string {
      let title = `${this.formatToMonthDay(startDate)}-${this.formatToDayYear(endDate)}`;
      if (`${this.formatToYear(startDate)}` !== `${this.formatToYear(endDate)}`) title = `${this.formatToFullDate(startDate)} - ${this.formatToFullDate(endDate)}`;
      if (`${this.formatToMonth(startDate)}` !== `${this.formatToMonth(endDate)}`) title = `${this.formatToMonthDay(startDate)} - ${this.formatToFullDate(endDate)}`;
      return title;
    },
    formatCalendarTitle(startDate: string, endDate: string): void {
      if (this.type === 'day') this.calendarTitle = this.formatToFullDate(startDate);
      if (this.type === 'week') this.calendarTitle = this.formatCalendarWeekTitle(startDate, endDate);
      if (this.type === 'month') this.calendarTitle = this.calendarInstance.title;
    },
    getViewType(type: string): void {
      this.type = type;
    },
    viewDay({ date }: { date: string}): void {
      this.focus = date;
      this.type = 'day';
    },
    setToday(): void {
      this.focus = '';
    },
    prev(): void {
      this.calendarInstance.prev();
    },
    next(): void {
      this.calendarInstance.next();
    },
    showEvent({ event }: { event: ICalendarEvent }) {
      if (this.isUseDefaultEventModal) this.showEventModal = true;
      this.eventInView = event;
      this.$emit('on-show-event-click', event);
    },
    handleEventModalClose(): void {
      if (this.isUseDefaultEventModal) this.showEventModal = false;
      this.eventInView = {} as ICalendarEvent;
    },
    updateRange({ start, end }: {start: CalendarTimestamp, end: CalendarTimestamp}) {
      this.formatCalendarTitle(start.date, end.date);
      const min = new Date(`${start.date}T00:00:00`);
      // This will include tours booked until 23:59:59 of the selected end date
      const max = new Date(`${dayjs(end.date).add(1, 'day').format('YYYY-MM-DD')}T00:00:00`);
      this.$emit('on-date-range-change', { min, max });
    },
    handleEventBlockEdit() {
      this.$emit('on-edit-event', { events: this.events, event: this.eventInView });
    },
    handleEventBlockDelete() {
      this.$emit('on-delete-event', { events: this.events, event: this.eventInView });
      this.showEventModal = false;
    },
    handleSyncButtonClick(): void {
      this.$emit('on-sync-button-click', { data: this.calendarDetails });
    },
    handleShowEventModal() {
      this.$emit('on-show-event-modal', { event: this.eventInView });
    },
  },
});
</script>
<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.calendar-header {
  background-color: white;
  margin-left: 1px !important;
  padding-top: 4px;
}
.calendar-header-month {
  position: sticky !important;
  top: 0;
  z-index: 4;
}
.event-default {
  background-color: #E6F3FA;
  border-left: 5px solid $vibrant-blue;
  border-top: 1px solid $vibrant-blue;
  border-right: 1px solid $vibrant-blue;
  border-bottom: 1px solid $vibrant-blue;
}
.event-exception {
  background-color: #B9B9B9;
  border-left: 5px solid #0D0D0D;
  border-top: 1px solid #0D0D0D;
  border-right: 1px solid #0D0D0D;
  border-bottom: 1px solid #0D0D0D;
}
.event-block {
  padding-top: 4px;
  padding-bottom: 4.5px;
  border-radius: 3px;
  height: 100%;
}
.rs-calendar {
  &__sync {
    color: $secondary-color;
  }
  &__arrow {
    width: 16px;
    font-weight: 400;
    height: 16px;
    color: $secondary-color
  }
  &__title {
    color: $secondary-color-dark;
    font-size: 22px;
    font-family: $body-font-family;
    font-weight: bold;
    color: $secondary-color-dark;
    white-space: nowrap;
  }
  &-event {
    &--truncate {
      text-overflow: ellipsis;
      overflow: hidden;
      line-height: 1.3;
    }
  }
}
::v-deep .v-event-timed  {
  border: none!important;
  background-color: transparent;
}

::v-deep .theme--light.v-calendar-events .v-event-timed {
  border: none!important;
}

::v-deep .v-past {
  .v-event {
    max-height: 100%;
    height: 42px!important;
  }
  .v-calendar-daily_head-day-label button, .v-calendar-weekly__day-label button {
    background-color: transparent!important;
     .v-btn__content {
        font-weight: bold;
      }
  }

}

::v-deep .v-future {
  .v-event {
    max-height: 100%;
    height: 42px!important;
  }
.v-calendar-daily_head-day-label button, .v-calendar-weekly__day-label button {
    background-color: transparent!important;
     .v-btn__content {
        font-weight: bold;
      }
  }
}

::v-deep .v-present {
  .v-event {
    max-height: 100%;
    height: 42px!important;
  }
  .v-calendar-daily_head-day-label, .v-calendar-weekly__day-label {
    button {
      background-color: $primary-color;
      border-radius: 5px;
      height: 19px;
      .v-btn__content {
        font-weight: bold;
      }
    }
  }
  .v-calendar-daily_head-day-label button {
    margin-top: 18px;
  }
  .v-calendar-weekly__day-label button {
    margin-top: 13px;
    margin-bottom: 9px;
  }
}

::v-deep .v-calendar-weekly__head, ::v-deep .v-calendar-daily_head-weekday {
  border-bottom: 1px solid rgba(0,0,0,.19);
}
::v-deep .v-calendar-weekly__head-weekday, ::v-deep .v-calendar-daily_head-weekday {
  font-weight: bold;
  color: #2C2254;
  font-size: 14px;
  text-transform: capitalize;
  color: #2C2254!important;
}

::v-deep .v-calendar-daily__day {
  .event-default {
    max-width: 780px;
  }
}

::v-deep .v-calendar-daily_head-day {
  .v-event {
    max-width: 780px!important;
  }
}

::v-deep .v-calendar-daily__interval-text {
  font-size: 12px;
}

::v-deep .v-calendar-weekly__week {
  min-height: var(--box-min-height-week) !important;
}

::v-deep .v-calendar-weekly__day {
  min-height:  var(--box-min-height-day) !important;
}

::v-deep .v-event-more {
  background-color: transparent !important;
}

::v-deep .v-calendar-weekly__head{
  position: sticky;
  background-color: white !important;
  top: 44px;
  width: 100%;
  z-index: 4;
}

::v-deep .v-calendar-weekly__head-weekday {
  background-color: white !important;
}

.btn-no-border {
  border-radius: 0px;
}
.btn-no-border-left {
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
}
.btn-no-border-right {
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
}

</style>
